import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';

// In-flight guard to prevent duplicate parallel requests for same domain id
const inFlightDomainIds = new Set<string>();

export interface DomainDetails {
  id: string;
  brand_name?: string; // Primary brand label we display (normalized from brand_name|brandName)
  aliases?: string[]; // Alternative names for the brand
  status?: string; // e.g. 'buildingbp', 'reviewbp', etc.
  excluded_competitors?: string[]; // Competitors the user chose to hide from tracking
}

export type DaysOption = 1 | 7 | 30 | 90;



export interface DomainState {
  selectedDomainId: string | null;
  current: DomainDetails | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastLoadedAt: number | null;
  selectedDays: DaysOption;
  overviewById: Record<string, unknown>;
  overviewStatusById: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  overviewErrorById: Record<string, string | undefined>;
  detailsById: Record<string, DomainDetails>; // cache per id
  detailsStatusById: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  detailsErrorById: Record<string, string | undefined>;
}

const initialState: DomainState = {
  selectedDomainId: null,
  current: null,
  status: 'idle',
  error: null,
  lastLoadedAt: null,
  selectedDays: 7,
  overviewById: {},
  overviewStatusById: {},
  overviewErrorById: {},
  detailsById: {},
  detailsStatusById: {},
  detailsErrorById: {},
};

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

// Extract a reasonable domain-shaped object from a variety of payload wrappers.
// Supports: { data: [...] }, { data: {...} }, [...], or direct object.
function pickDomainObject(payload: unknown): Record<string, unknown> | null {
  if (isPlainObject(payload) && Array.isArray(payload.data) && payload.data.length) {
    return (payload.data as unknown[])[0] as Record<string, unknown>;
  }
  if (isPlainObject(payload) && isPlainObject(payload.data)) {
    return payload.data as Record<string, unknown>;
  }
  if (Array.isArray(payload) && payload.length && isPlainObject(payload[0])) {
    return payload[0] as Record<string, unknown>;
  }
  if (isPlainObject(payload)) return payload;
  return null;
}

function pickBrandName(domainObj: Record<string, unknown>): string | undefined {
  const v1 = domainObj.brand_name;
  const v2 = (domainObj as Record<string, unknown>).brandName as unknown;
  if (typeof v1 === 'string' && v1.trim()) return v1.trim();
  if (typeof v2 === 'string' && v2.trim()) return v2.trim();
  return undefined;
}

function pickAliases(domainObj: Record<string, unknown>): string[] | undefined {
  const v = domainObj.aliases;
  if (Array.isArray(v)) {
    const filtered = v.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return filtered.length > 0 ? filtered : undefined;
  }
  return undefined;
}

function pickExcludedCompetitors(domainObj: Record<string, unknown>): string[] | undefined {
  const v = domainObj.excluded_competitors;
  if (Array.isArray(v)) {
    const filtered = v.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return filtered.length > 0 ? filtered : undefined;
  }
  return undefined;
}

function normalizeDomainDetails(payload: unknown, id: string): DomainDetails | null {
  const obj = pickDomainObject(payload);
  if (!obj) return null;
  // Prefer response id if present and valid, else path id
  const respId = typeof obj.id === 'string' && obj.id.trim() ? (obj.id as string).trim() : id;
  const brand_name = pickBrandName(obj);
  const aliases = pickAliases(obj);
  // Extract status if present
  const status = typeof obj.status === 'string' && obj.status.trim() ? obj.status.trim() : undefined;
  const excluded_competitors = pickExcludedCompetitors(obj);
  return { id: respId, brand_name, aliases, status, excluded_competitors };
}

// Fetch domain details by id. Always refetch if id changes; simple caching could be added later.
export const fetchDomainById = createAsyncThunk<DomainDetails, string, { state: { domain: DomainState } }>(
  'domain/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${id}`);
      const payload = res.data;
      if (process.env.NODE_ENV !== 'production') {
        // Debug log to inspect raw structure (safe: only in dev)
        console.debug('[domain] fetchDomainById raw response', payload);
      }
      const details = normalizeDomainDetails(payload, id);
      if (!details) return rejectWithValue('Domain not found');
      return details;
    } catch (err: unknown) {
      if (typeof err === 'object' && err && 'response' in err) {
        // @ts-expect-error dynamic access
        const msg = err.response?.data?.message;
        return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to load domain');
      }
      return rejectWithValue('Failed to load domain');
    }
  },
  {
    condition: (id: string, { getState }) => {
      const { detailsStatusById } = getState().domain;
      // Allow refetching even if cached, only block if currently loading
      if (detailsStatusById[id] === 'loading') return false; 
      if (inFlightDomainIds.has(id)) return false; 
      inFlightDomainIds.add(id);
      return true;
    }
  }
);

// Fetch overview for a domain id with status gating (only once unless we decide to add TTL refresh later)
export const fetchDomainOverview = createAsyncThunk<{ id: string; days: number; data: unknown }, { id: string; days?: number }, { state: { domain: DomainState } }>(
  'domain/fetchOverview',
  async ({ id, days = 7 }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${id}/overviews`, { params: { days } });
      return { id, days, data: res.data };
    } catch (err: unknown) {
      if (typeof err === 'object' && err && 'response' in err) {
        // @ts-expect-error dynamic access
        const msg = err.response?.data?.message;
        return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to load overview');
      }
      return rejectWithValue('Failed to load overview');
    }
  },
  {
    condition: ({ id, days = 7 }: { id: string; days?: number }, { getState }) => {
      const { overviewStatusById, overviewById } = getState().domain;
      const cacheKey = `${id}:${days}`;
      const st = overviewStatusById[cacheKey];
      // Block if already loading this exact (domain, days) combo
      if (st === 'loading') return false;
      // Cache hit — skip fetch
      if (st === 'succeeded' && overviewById[cacheKey] !== undefined) return false;
      return true;
    }
  }
);

// New action to update domain status
export const updateDomainStatus = createAsyncThunk<{ id: string; status: string }, { id: string; status: string }, { state: { domain: DomainState } }>(
  'domain/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      await apiClient.put(`/v1/domains/${id}`, { status });
      return { id, status };
    } catch (err: unknown) {
      if (typeof err === 'object' && err && 'response' in err) {
        // @ts-expect-error dynamic access
        const msg = err.response?.data?.message;
        return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to update domain status');
      }
      return rejectWithValue('Failed to update domain status');
    }
  }
);

// Update excluded competitors list for a domain
export const updateExcludedCompetitors = createAsyncThunk<
  { id: string; excluded_competitors: string[] },
  { id: string; excluded_competitors: string[] },
  { state: { domain: DomainState } }
>(
  'domain/updateExcludedCompetitors',
  async ({ id, excluded_competitors }, { rejectWithValue }) => {
    try {
      await apiClient.put(`/v1/domains/${id}`, { excluded_competitors });
      return { id, excluded_competitors };
    } catch (err: unknown) {
      if (typeof err === 'object' && err && 'response' in err) {
        // @ts-expect-error dynamic access
        const msg = err.response?.data?.message;
        return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to update excluded competitors');
      }
      return rejectWithValue('Failed to update excluded competitors');
    }
  }
);

const domainSlice = createSlice({
  name: 'domain',
  initialState,
  reducers: {
    selectDomain(state, action: PayloadAction<string | null>) {
      state.selectedDomainId = action.payload;
      if (action.payload === null) {
        // Clear current domain when deselecting (welcome page / logout / domain change)
        state.current = null;
        state.status = 'idle';
        state.error = null;
        state.lastLoadedAt = null;
        // Do not wipe cached overviews to allow quick back-and-forth switching; comment next lines to clear instead.
      } else {
        const cached = state.detailsById[action.payload];
        if (cached) {
          state.current = cached;
          state.status = 'succeeded';
          state.error = null;
        }
      }
    },
    clearDomain(state) {
      state.selectedDomainId = null;
      state.current = null;
      state.status = 'idle';
      state.error = null;
      state.lastLoadedAt = null;
      state.selectedDays = 7;
      state.overviewById = {};
      state.overviewStatusById = {};
      state.overviewErrorById = {};
    },
    setSelectedDays(state, action: PayloadAction<DaysOption>) {
      state.selectedDays = action.payload;
      // Sync overview cache: copy domainId:days → domainId so selectors see the right data
      if (state.selectedDomainId) {
        const ck = `${state.selectedDomainId}:${action.payload}`;
        if (state.overviewById[ck] !== undefined) {
          state.overviewById[state.selectedDomainId] = state.overviewById[ck];
          state.overviewStatusById[state.selectedDomainId] = 'succeeded';
          state.overviewErrorById[state.selectedDomainId] = undefined;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDomainById.pending, (state, action) => {
        const id = action.meta.arg;
        state.detailsStatusById[id] = 'loading';
        state.detailsErrorById[id] = undefined;
        if (state.selectedDomainId === id) {
          state.status = 'loading';
          state.error = null;
        }
      })
      .addCase(fetchDomainById.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.detailsStatusById[id] = 'succeeded';
        state.detailsErrorById[id] = undefined;
        state.detailsById[id] = action.payload;
        if (state.selectedDomainId === id) {
          state.status = 'succeeded';
          state.current = action.payload;
          state.error = null;
          state.lastLoadedAt = Date.now();
        }
        inFlightDomainIds.delete(id);
      })
      .addCase(fetchDomainById.rejected, (state, action) => {
        const id = action.meta.arg;
        state.detailsStatusById[id] = 'failed';
        const msg = typeof action.payload === 'string' ? action.payload : 'Unknown error';
        state.detailsErrorById[id] = msg;
        if (state.selectedDomainId === id) {
          state.status = 'failed';
          state.error = msg;
        }
        inFlightDomainIds.delete(id);
      });
    builder
      .addCase(fetchDomainOverview.pending, (state, action) => {
        const { id, days = 7 } = action.meta.arg;
        const ck = `${id}:${days}`;
        state.overviewStatusById[ck] = 'loading';
        state.overviewErrorById[ck] = undefined;
        // Only mirror to simple key if this is the active time range
        if (days === state.selectedDays) {
          state.overviewStatusById[id] = 'loading';
        }
      })
      .addCase(fetchDomainOverview.fulfilled, (state, action) => {
        const { id, days, data } = action.payload || {};
        if (id) {
          const ck = `${id}:${days}`;
          state.overviewById[ck] = data;
          state.overviewStatusById[ck] = 'succeeded';
          state.overviewErrorById[ck] = undefined;
          // Only mirror to simple key if this matches the currently selected time range
          if (days === state.selectedDays) {
            state.overviewById[id] = data;
            state.overviewStatusById[id] = 'succeeded';
            state.overviewErrorById[id] = undefined;
          }
        }
      })
      .addCase(fetchDomainOverview.rejected, (state, action) => {
        const { id, days = 7 } = action.meta.arg;
        const ck = `${id}:${days}`;
        const msg = typeof action.payload === 'string' ? action.payload : 'Unknown error';
        state.overviewStatusById[ck] = 'failed';
        state.overviewErrorById[ck] = msg;
        if (days === state.selectedDays) {
          state.overviewStatusById[id] = 'failed';
          state.overviewErrorById[id] = msg;
        }
      });
    builder
      .addCase(updateExcludedCompetitors.fulfilled, (state, action) => {
        const { id, excluded_competitors } = action.payload;
        const cached = state.detailsById[id];
        if (cached) {
          cached.excluded_competitors = excluded_competitors;
        }
        if (state.current && state.current.id === id) {
          state.current.excluded_competitors = excluded_competitors;
        }
      });
  },
});

export const { selectDomain, clearDomain, setSelectedDays } = domainSlice.actions;
export default domainSlice.reducer;
